import axios from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';

import { DEFAULT_LANGUAGE } from 'constants/localization';

import { DEFAULT_AUTH_TOKEN_NAME } from 'services/api/ApiClientService/constants';

import { getErrorHandler } from './helpers/errors';
import { onFulfilledRequest, onFulfilledResponse, onRejectedRequest, onRejectedResponse } from './helpers/interceptor';
import { countRetryDelay, retryHandler } from './helpers/retry';
import { ApiClientInstance, FailedRequestType, InstallationPropsType } from './types';

const initApiClient = ({
    apiUrl: baseURL,
    apiVersion,
    apiKey,
    retries = 0,
    authTokenKey = DEFAULT_AUTH_TOKEN_NAME,
    headerTokenKey,
    onUpdateToken,
    onRetry,
    onErrorHandler,
    onAuthorisationError,
    getAuthToken,
    retriesDelay,
    timeout = 0,
    headers = {},
}: InstallationPropsType): ApiClientInstance => {
    const errorHandler = getErrorHandler(onErrorHandler);
    const serverErrorUrls: Set<string> = new Set();
    const isServerErrorModalOpened = { current: false };
    const isTokenRefreshInProgress = { current: false };
    const failedRequestsRequiredToken = { current: [] as FailedRequestType[] };

    const instance = axios.create({
        baseURL,
        timeout,
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            version: apiVersion,
            language: DEFAULT_LANGUAGE,
            ...headers,
        },
    });

    instance.interceptors.request.use(
        onFulfilledRequest({ errorHandler, authTokenKey, getAuthToken, headerTokenKey }),
        onRejectedRequest({ errorHandler })
    );

    instance.interceptors.response.use(
        onFulfilledResponse({ onUpdateToken, headerTokenKey, serverErrorUrls, isServerErrorModalOpened }),
        onRejectedResponse({
            errorHandler,
            onAuthorisationError,
            serverErrorUrls,
            isServerErrorModalOpened,
            isTokenRefreshInProgress,
            failedRequestsRequiredToken,
            instance,
            headerTokenKey,
        })
    );

    if (retries) {
        axiosRetry(instance, {
            retries,
            onRetry: retryHandler(onRetry),
            retryDelay: retriesDelay ? () => retriesDelay : countRetryDelay,
            retryCondition: isNetworkOrIdempotentRequestError,
            shouldResetTimeout: true,
        });
    }

    return {
        __instance__: instance,
        get: instance.get.bind(instance),
        post: instance.post.bind(instance),
        put: instance.put.bind(instance),
        patch: instance.patch.bind(instance),
        delete: instance.delete.bind(instance),
    };
};

export { initApiClient };
